import type { QueriesResults } from "@tanstack/react-query";
import { useQueries } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { selectIsInitialFiltersReady } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/selectors";
import {
    isLeadsType,
    type LeadsSearchResponseDto,
    type LeadsType,
    selectIsLeadsSearchDtoReady,
    selectSearchResultsDtoByLeadsType,
} from "app/pages/sales-intelligence/modules/leads-search";
import { useIsLeadsCriticalDataLoading } from "app/pages/sales-intelligence/modules/leads-search/hooks/useIsLeadsCriticalDataLoading";
import { useSearchDemoMode } from "app/pages/sales-intelligence/modules/leads-search/hooks/useSearchDemoMode";
import { fetchSearchQuotaThunk } from "app/pages/sales-intelligence/sub-modules/advanced-search/store/effects";

import type { PageTabsConfig } from "../../types/page-tabs";

import { createLeadsSearchResultsQuery } from "./options";

type RawLeadsQueries = QueriesResults<LeadsSearchResponseDto[]>;

const useLeadsSearchResultsQueries = (tabsConfig: PageTabsConfig): RawLeadsQueries => {
    const dispatch = useDispatch();

    // Hold the search until the route's terminal initial writer has committed (plain seed, territory overlay,
    // or saved search). This is set atomically with the overlay/filter write, so gating on it fires the search
    // once with the final DTO instead of once with the seeded default and again after the overlay lands.
    const isInitialFiltersReady = useSelector(selectIsInitialFiltersReady);
    const isLeadsSearchDtoReady = useSelector(selectIsLeadsSearchDtoReady);
    const searchResultsDtoByLeadsType = useSelector(selectSearchResultsDtoByLeadsType);

    const isLeadsCriticalDataLoading = useIsLeadsCriticalDataLoading();
    const demoMode = useSearchDemoMode();

    const leadsTabs = tabsConfig.filter(({ id }) => isLeadsType(id)) as PageTabsConfig<LeadsType>;

    return useQueries({
        queries: leadsTabs.map(({ id }) =>
            createLeadsSearchResultsQuery({
                leadsType: id,
                dto: searchResultsDtoByLeadsType[id],
                demoMode,
                enabled: isInitialFiltersReady && isLeadsSearchDtoReady && !isLeadsCriticalDataLoading,
                // The reader twin (`useLeadsSearchResult`) observes this same key and is what surfaces the
                // error to the boundary; a background tab must not tear the page down on its own.
                throwOnError: false,
                onQuotaError: () => void dispatch(fetchSearchQuotaThunk()),
            }),
        ),
    });
};

export default useLeadsSearchResultsQueries;
